import {CenterBarToDrawerShell} from './center-bar-to-drawer-shell'
import {parsePreviewShellConfig} from '@/lib/site-agent/placements/parse-shell-config'

import {fetchResolvedShellConfig, parseShellConfig, resolveFormFactor, type SdkConfigRequestResource} from './config'
import {EmbeddedToSheetShell} from './embedded-to-sheet-shell'
import {getEnvironmentConfig} from './environment'
import {ExternalTriggerShell} from './external-trigger-shell'
import {FloatingButtonShell} from './floating-button-shell'
import {AnagramOpenEventName, type AnagramOpenEventDetail} from './host-events'
import {InlineShell} from './inline-shell'
import {resolveParentUrlFromBootScript, hasExplicitParentUrlFromBootScript} from './resolve-parent-url'
import type {SdkLiveResource, Shell} from './shell-base'
import {applySideSheetLayout, requestsSideSheetLayout, supportsSideSheetLayout} from './side-sheet-layout'
import {
  type CenterBarToDrawerConfig,
  type EmbeddedToSheetConfig,
  type FormFactor,
  FormFactors,
  type ShellConfig,
} from './types'

type OpenableShell = Shell & {
  openPanel: () => void
}

function isOpenableShell(shell: Shell): shell is OpenableShell {
  return 'openPanel' in shell && typeof shell.openPanel === 'function'
}

export class AnagramSDK {
  private readonly currentScript: HTMLScriptElement
  private readonly config: ReturnType<typeof getEnvironmentConfig>
  private readonly scriptEmbedId: string
  private embedId: string
  private liveResource: SdkLiveResource
  private readonly configResource: SdkConfigRequestResource
  private readonly isLegacyFormat: boolean
  private readonly isFromPreview: boolean
  private readonly hasExplicitParentUrl: boolean
  private readonly parentUrl: string | undefined
  private readonly requestsSideSheetLayout: boolean
  private mountedShell: Shell | null = null
  private cleanupObserver: MutationObserver | null = null
  private previewShellConfig: ShellConfig | null = null
  private previewConfigListener: ((event: MessageEvent) => void) | null = null
  private openEventListener: ((event: Event) => void) | null = null
  private pendingOpen = false

  constructor(currentScript: HTMLScriptElement) {
    this.currentScript = currentScript
    this.config = getEnvironmentConfig(currentScript)
    const scriptUrl = new URL(currentScript.src)

    const placementId = currentScript.getAttribute('data-placement')
    const dataExperienceId = currentScript.getAttribute('data-experience')
    const experienceId = dataExperienceId || scriptUrl.searchParams.get('experienceID')
    const embedId = placementId ?? experienceId

    if (!embedId) {
      throw new Error(
        '[Anagram] Missing placement ID. Use data-placement (or legacy data-experience) attribute or experienceID URL param.'
      )
    }

    this.scriptEmbedId = embedId
    this.embedId = embedId
    this.liveResource = placementId ? 'placements' : 'experiences'
    this.configResource = placementId ? 'placements' : experienceId ? 'auto' : 'experiences'
    this.isLegacyFormat = !placementId && !dataExperienceId
    this.isFromPreview = scriptUrl.searchParams.get('fromPreview') === 'true'
    this.hasExplicitParentUrl = hasExplicitParentUrlFromBootScript(currentScript)
    this.parentUrl = resolveParentUrlFromBootScript(currentScript)
    this.requestsSideSheetLayout = requestsSideSheetLayout(currentScript)
    this.previewShellConfig = parsePreviewShellConfig(
      currentScript.getAttribute('data-preview-config'),
      this.isFromPreview
    )
  }

  async boot(): Promise<void> {
    try {
      this.destroy()
      this.setupPreviewConfigListener()
      this.setupOpenEventListener()

      const formFactorOverride = this.getFormFactorOverride()
      let shellConfig: ShellConfig | null = null
      if (this.isFromPreview && this.previewShellConfig) {
        shellConfig = this.previewShellConfig
      } else {
        const resolvedConfig = await fetchResolvedShellConfig(this.config.baseUrl, this.embedId, {
          parentUrl: this.parentUrl,
          formFactorOverride: formFactorOverride ?? undefined,
          fromPreview: this.isFromPreview,
          resource: this.configResource,
        })
        shellConfig = resolvedConfig?.shellConfig ?? null
        if (resolvedConfig) {
          this.embedId = resolvedConfig.embedId
          this.liveResource = resolvedConfig.resource
        }
      }

      if (!shellConfig) {
        if (this.isLegacyFormat) {
          console.warn('[Anagram] Missing shell config for legacy embed. Falling back to inline mount.')
          new InlineShell(this.config, this.embedId, this.currentScript, {
            liveResource: this.liveResource,
            fromPreview: this.isFromPreview,
            hasExplicitParentUrl: this.hasExplicitParentUrl,
            parentUrl: this.parentUrl,
          }).mount()
          return
        }
        console.warn('[Anagram] Missing shell config. Skipping mount.')
        return
      }

      if (formFactorOverride && formFactorOverride !== shellConfig.formFactor) {
        console.warn(
          `[Anagram] Form factor override "${formFactorOverride}" did not match API response. Skipping mount.`
        )
        return
      }

      this.mountShell(shellConfig)
    } catch (error) {
      console.error('[Anagram] Failed to initialize:', error)
    }
  }

  destroy(): void {
    this.previewConfigListener && window.removeEventListener('message', this.previewConfigListener)
    this.previewConfigListener = null
    this.openEventListener && window.removeEventListener(AnagramOpenEventName, this.openEventListener)
    this.openEventListener = null
    this.pendingOpen = false
    this.cleanupObserver?.disconnect()
    this.cleanupObserver = null
    this.mountedShell?.destroy()
    this.mountedShell = null
  }

  private setupPreviewConfigListener(): void {
    if (!this.isFromPreview || this.previewConfigListener) {
      return
    }

    this.previewConfigListener = (event: MessageEvent) => {
      const data = event.data as {type?: string; embedId?: string; config?: unknown} | undefined
      if (data?.type !== 'ANAGRAM_PREVIEW_CONFIG' || data.embedId !== this.embedId) {
        return
      }

      try {
        const shellConfig = parseShellConfig(data.config as Record<string, unknown>)
        const previousFormFactor = this.previewShellConfig?.formFactor
        this.previewShellConfig = shellConfig

        if (
          this.mountedShell &&
          previousFormFactor === shellConfig.formFactor &&
          this.mountedShell.applyPreviewShellConfig(shellConfig)
        ) {
          return
        }

        this.mountedShell?.destroy()
        this.mountedShell = null
        this.mountShell(shellConfig)
      } catch (error) {
        console.warn('[Anagram] Invalid preview config update:', error)
      }
    }

    window.addEventListener('message', this.previewConfigListener)
  }

  private setupOpenEventListener(): void {
    if (this.openEventListener) {
      return
    }

    this.openEventListener = (event: Event) => {
      const placementId = (event as CustomEvent<Partial<AnagramOpenEventDetail> | undefined>).detail?.placementId
      // Merchants only know the ID from their pasted snippet, so accept it even after
      // config resolution rewrites `embedId` (e.g. a legacy `data-experience` alias).
      if (placementId !== this.scriptEmbedId && placementId !== this.embedId) {
        return
      }

      if (this.mountedShell) {
        if (isOpenableShell(this.mountedShell)) {
          this.mountedShell.openPanel()
        }
        return
      }

      // Config is still loading; replay the open once the shell mounts.
      this.pendingOpen = true
    }

    window.addEventListener(AnagramOpenEventName, this.openEventListener)
  }

  private getFormFactorOverride(): FormFactor | null {
    const raw = this.currentScript.getAttribute('data-form-factor')
    if (!raw) return null
    const valid = Object.values(FormFactors) as string[]
    if (valid.includes(raw)) return raw as FormFactor
    const resolved = resolveFormFactor(raw)
    return valid.includes(resolved) ? (resolved as FormFactor) : null
  }

  private mountShell(shellConfig: ShellConfig): void {
    let shell: Shell
    const sideSheetLayout = this.requestsSideSheetLayout && supportsSideSheetLayout(shellConfig)
    const resolvedConfig = sideSheetLayout ? applySideSheetLayout(shellConfig) : shellConfig
    const shellOptions = {
      fromPreview: this.isFromPreview,
      hasExplicitParentUrl: this.hasExplicitParentUrl,
      liveResource: this.liveResource,
      parentUrl: this.parentUrl,
      previewConfig: this.isFromPreview ? resolvedConfig : undefined,
      sideSheetLayout,
    }

    switch (resolvedConfig.formFactor) {
      case FormFactors.Fab: {
        if (document.getElementById('anagram-shell')) {
          console.warn('[Anagram] Floating button already exists on this page. Falling back to embedded.')
          shell = new InlineShell(this.config, this.embedId, this.currentScript, shellOptions)
          break
        }
        this.prefetchLiveFrame()
        shell = new FloatingButtonShell(this.config, this.embedId, resolvedConfig, shellOptions)
        break
      }
      case FormFactors.EmbeddedToSheet: {
        this.prefetchLiveFrame()
        shell = new EmbeddedToSheetShell(
          this.config,
          this.embedId,
          resolvedConfig as EmbeddedToSheetConfig,
          this.currentScript,
          shellOptions
        )
        break
      }
      case FormFactors.CenterBarToDrawer: {
        this.prefetchLiveFrame()
        shell = new CenterBarToDrawerShell(
          this.config,
          this.embedId,
          resolvedConfig as CenterBarToDrawerConfig,
          shellOptions
        )
        break
      }
      case FormFactors.ExternalTrigger: {
        shell = new ExternalTriggerShell(this.config, this.embedId, resolvedConfig, shellOptions)
        break
      }
      default:
        shell = new InlineShell(this.config, this.embedId, this.currentScript, shellOptions)
        break
    }

    this.mountedShell = shell
    shell.mount()
    if (this.pendingOpen) {
      this.pendingOpen = false
      if (isOpenableShell(shell)) {
        shell.openPanel()
      }
    }
    this.observeScriptLifecycle()
  }

  private prefetchLiveFrame(): void {
    const params = new URLSearchParams({mode: 'sheet'})
    if (this.isFromPreview) {
      params.set('fromPreview', 'true')
    }
    const url = `${this.config.baseUrl}/live/${this.liveResource}/${this.embedId}?${params.toString()}`
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        const link = document.createElement('link')
        link.rel = 'prefetch'
        link.href = url
        document.head.appendChild(link)
      })
    }
  }

  private observeScriptLifecycle(): void {
    this.cleanupObserver?.disconnect()

    if (!this.currentScript.isConnected) {
      this.destroy()
      return
    }

    this.cleanupObserver = new MutationObserver(() => {
      if (this.currentScript.isConnected) return
      this.destroy()
    })

    this.cleanupObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    })
  }
}
